import React from 'react';
import { Page, Text, View, Document, StyleSheet, Font, Image } from '@react-pdf/renderer';
import { abreviarNome, formatToDate } from '../Components/jwtUtils';
import QRCode from 'qrcode';

Font.register({ family: 'Oswald', src: '../../assets/fonts/bold.ttf' });
Font.register({ family: 'Whisper', src: '../../assets/fonts/w-regular.ttf' });
Font.register({ family: 'Cursiva', src: '../../assets/fonts/letra.ttf' });

// Função auxiliar para construir URLs de imagens
const getImageUrl = (path) => {
    if (!path) return null;
    
    // Se já começa com http/https, retorna direto
    if (path.startsWith('http://') || path.startsWith('https://')) {
        return path;
    }
    
    // Se começa com '/', remove a barra inicial para evitar duplicação
    let cleanPath = path.startsWith('/') ? path.substring(1) : path;
    
    // Remove duplicação se o path já inclui 'imagens/' no início
    const baseUrl = process.env.REACT_APP_IMG_URL;
    
    // Normalizar baseUrl removendo barra final para verificação
    const baseUrlNormalized = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    
    if ((baseUrlNormalized.endsWith('/imagens') || baseUrlNormalized.endsWith('imagens')) && cleanPath.startsWith('imagens/')) {
        cleanPath = cleanPath.substring(8); // Remove 'imagens/' do início
    }
    
    // Constrói a URL completa, garantindo apenas uma barra
    const separator = baseUrl.endsWith('/') ? '' : '/';
    return `${baseUrl}${separator}${cleanPath}`;
};

const styles = StyleSheet.create({
    page: {
        flexDirection: 'row',
        backgroundColor: '#E4E4E4',
        padding: 1,
    },
    diploma: {
        position: 'relative',
        padding: 1,
        margin: 10,
        flex: 1,
    },
    backgroundImage: {
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: -1,
    },
    title: {
        fontFamily: 'Oswald',
        fontSize: 42,
        textAlign: 'center',
        marginBottom: 20,
        color: '#f97a6d'
    },
    message: {
        fontFamily: 'Oswald',
        fontSize: 14,
        textAlign: 'center',
        marginVertical: 40,
        width: '550px',
        marginLeft: '135px',
        marginTop: '5px',
        color: '#3F3F3F'
    },
    prazo_validade: {
        fontFamily: 'Oswald',
        fontSize: 12,
        textAlign: 'center',
        marginVertical: 120,
        width: '150px',
        marginLeft: '110px',
        color: '#3F3F3F',
        position: 'absolute',
        bottom: 40,
    },
    assinatura: {
        fontFamily: 'Oswald',
        fontSize: 12,
        textAlign: 'center',
        marginVertical: 40,
        width: '150px',
        marginLeft: '130px',
        color: '#3F3F3F',
        position: 'absolute',
        bottom: 40,
        borderBottom: '1px solid #c0bfc0',
    },
    participante: {
        fontSize: 11,
        textAlign: 'center',
        marginVertical: 40,
        width: '150px',
        marginLeft: '130px',
        color: '#3F3F3F',
        position: 'absolute',
        bottom: 25,
    },
    footer: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-end',
    },
    signatureBox: {
        borderStyle: 'dashed',
        borderWidth: 1,
        borderColor: '#95A5A6',
        width: '30%',
        height: 80,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: 50,
    },
    signatureText: {
        color: '#7F8C8D',
        fontSize: 14,
        textAlign: 'center',
    }
});

// Monta o texto do certificado. Se a empresa definiu um modelo (textoCertificado),
// substitui os marcadores pelos dados reais; caso contrário usa o template padrão.
const montarTextoCertificado = (template, data, empresa, formatarCPF) => {
    const cargaHoraria = `${data.cargaHorariaTotal} ${data.cargaHorariaTotal === 1 ? "hora" : "horas"}`;
    const cpfFormatado = data.cpf ? formatarCPF(data.cpf) : '';

    if (template && typeof template === 'string' && template.trim() !== '') {
        const valores = {
            '{aluno}': data.nomeAluno ?? '',
            '{cpf}': cpfFormatado,
            '{curso}': data.nomeCurso ?? '',
            '{empresa}': empresa ?? '',
            '{unidade}': data.unidade ?? '',
            '{data}': formatToDate(data.data),
            '{topico}': data.nomeTopico ?? '',
            '{carga_horaria}': cargaHoraria,
        };
        return template.replace(/\{aluno\}|\{cpf\}|\{curso\}|\{empresa\}|\{unidade\}|\{data\}|\{topico\}|\{carga_horaria\}/g, (m) => valores[m] ?? '');
    }

    // Template padrão (com/sem CPF) — mantém o comportamento histórico.
    return data.cpf
        ? `Certificamos que ${data.nomeAluno}, identificado(a) com o CPF ${cpfFormatado}, concluiu com aproveitamento satisfatório o Curso sobre ${data.nomeCurso}, promovida nas dependências da empresa ${empresa} na unidade de ${data.unidade} na data ${formatToDate(data.data)}, conforme exigências da ${data.nomeTopico}, com carga horária de ${cargaHoraria}.`
        : `Certificamos que ${data.nomeAluno}, concluiu com aproveitamento satisfatório o Curso sobre ${data.nomeCurso}, promovida nas dependências da empresa ${empresa} na unidade de ${data.unidade} na data ${formatToDate(data.data)}, conforme exigências da ${data.nomeTopico}, com carga horária de ${cargaHoraria}.`;
};

const GerarCertificado = ({ data, diplomaUrl, empresa, textoCertificado }) => {

    if (!data) return null;

    // Função para formatar CPF
    const formatarCPF = (cpf) => {
        if (!cpf) return '';
        // Remove caracteres não numéricos
        const cpfLimpo = cpf.replace(/\D/g, '');
        // Aplica máscara XXX.XXX.XXX-XX
        if (cpfLimpo.length === 11) {
            return cpfLimpo.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
        }
        // Se não tiver 11 dígitos, retorna o CPF original
        return cpf;
    };

    // Construir URL do diploma padrão
    const diplomaImage = (diplomaUrl && typeof diplomaUrl === 'string' && diplomaUrl.trim() !== '') 
        ? getImageUrl(diplomaUrl) 
        : "../../assets/diploma.png";
    
    // Função auxiliar para verificar se uma imagem é válida
    const getValidImageUrl = (imageUrl, fallback) => {
        // Verificar se a imagem é válida e não é null/undefined/string vazia
        if (imageUrl && 
            typeof imageUrl === 'string' && 
            imageUrl.trim() !== '' && 
            imageUrl !== 'null' && 
            imageUrl !== 'undefined') {
            return getImageUrl(imageUrl);
        }
        // Se não for válida, usar o fallback (que é o diploma padrão)
        return fallback;
    };

    // Função para verificar se tem imagem customizada
    const hasCustomImage = (imageUrl) => {
        return imageUrl && 
            typeof imageUrl === 'string' && 
            imageUrl.trim() !== '' && 
            imageUrl !== 'null' && 
            imageUrl !== 'undefined';
    };
    
    // Gerar QR Code como base64
    const generateQRCode = () => {
        try {
            const qrUrl = `${process.env.REACT_APP_URL}/validador?id=${data.id}`;
            return QRCode.toDataURL(qrUrl, { 
                width: 150,
                margin: 1,
                color: {
                    dark: '#000000',
                    light: '#FFFFFF'
                }
            });
        } catch (error) {
            console.error('Erro ao gerar QR Code:', error);
            return null;
        }
    };
    
    const qrCodeDataUrl = generateQRCode();

    return (
        <Document>
            <Page size="A4" orientation="landscape" style={styles.page}>
                <View style={styles.diploma}>
                <Image style={styles.backgroundImage} src={getValidImageUrl(data.certificado_frente, diplomaImage)} />
                    {/* ID do Certificado */}
                    <Text style={{
                        position: 'absolute',
                        top: 20,
                        right: 20,
                        fontSize: 10,
                        color: '#666666',
                        fontFamily: 'Oswald'
                    }}>
                        ID: {data.id}
                    </Text>
                    <Text style={{ marginTop: 240, marginLeft: 10, textAlign: 'center', fontSize: 28, color: "#3F3F3F", fontFamily: 'Oswald' }}>{data.nomeAluno}</Text>
                    <Text style={styles.message}>
                        {montarTextoCertificado(textoCertificado, data, empresa, formatarCPF)}
                    </Text>
                    {
                        data.prazo_validade !== 0 && data.prazo_validade !== null && 
                        <Text style={styles.prazo_validade}>
                            Prazo de Validade: {data.prazo_validade} {data.prazo_validade === 1 ? "mês" : "meses"}
                        </Text>
                    }
                    <Text style={styles.assinatura}>
                        {abreviarNome(data.nomeAluno)}
                    </Text>
                    <Text style={styles.participante}>
                        Participante
                    </Text>
                </View>
            </Page>
            <Page size="A4" orientation="landscape" style={{...styles.page, backgroundColor: '#FFFFFF'}}>
                <View style={{...styles.diploma, backgroundColor: '#FFFFFF'}}>
                    {/* Só renderiza imagem de fundo se houver certificado customizado */}
                    {hasCustomImage(data.certificado) && (
                        <Image style={styles.backgroundImage} src={getImageUrl(data.certificado)} />
                    )}
                    {/* QR Code para validação */}
                    {qrCodeDataUrl && (
                        <View style={{
                            position: 'absolute',
                            top: 80,
                            right: 110,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center'
                        }}>
                            <Image 
                                src={qrCodeDataUrl} 
                                style={{
                                    width: 40,
                                    height: 40,
                                    marginBottom: 10
                                }} 
                            />
                        </View>
                    )}
                    
                    {/* Texto de validação */}
                    <View style={{
                        position: 'absolute',
                        bottom: 20,
                        left: 50,
                        right: 200,
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        <Text style={{
                            fontSize: 8,
                            color: '#333333',
                            textAlign: 'left',
                            fontFamily: 'Oswald',
                            lineHeight: 1.3,
                            marginBottom: 5
                        }}>
                            Os certificados emitidos pela MFJ Treinamentos têm validade nacional e estão em conformidade com as Normas Regulamentadoras (NRs) do Ministério do Trabalho e Emprego, conforme a Portaria nº 3.214/1978. A autenticidade pode ser verificada pelo QR Code disponível no documento.
                        </Text>
                    </View>
                </View>
            </Page>
        </Document>
    )
};

export default GerarCertificado;